import apiClient from "modules/main/services/apiClient";
import { unavailableReplacementEndpoint } from "modules/main/services/endpoints";
import type { Payload, ProductSearchSelectionContext } from "modules/cart/types/cart";
import type { UnavailableAlternative } from "modules/cart/types/cart";

/**
 * Request body for PATCH /api/checkout/scenarios/{scenario_id}/unavailable-replacement.
 */
export interface UnavailableReplacementRequest {
  store_id: number;
  original_catalog_id: string;
  original_sku?: string;
  replacement_catalog_id: string;
  /** Concrete hashed SKU of the replacement product.
   * When provided, backend resolves by products.hashed_sku + store_id,
   * avoiding canonical-SKU ambiguity. */
  replacement_sku?: string;
}

/**
 * Response from the unavailable-replacement PATCH endpoint.
 */
export interface UnavailableReplacementResponse {
  scenario_id: number;
  store_id: number;
  original_catalog_id: string;
  replacement_catalog_id: string;
  replacement_sku?: string;
  replaced_sku: string;
  replaced_price: number;
}

/**
 * Persists an unavailable-product replacement for a checkout scenario.
 * Calls apiClient.patch imperatively.
 *
 * @param scenarioId - The scenario (order) ID from OptimizedScenario.order_id.
 * @param body - The replacement details.
 * @returns The backend response payload.
 */
export async function patchUnavailableReplacement(
  scenarioId: number,
  body: UnavailableReplacementRequest,
): Promise<UnavailableReplacementResponse> {
  return apiClient.patch<UnavailableReplacementResponse>(
    unavailableReplacementEndpoint(scenarioId),
    body,
  );
}

/**
 * Maps a product-search picker selection to an UnavailableAlternative.
 * Uses canonical identifiers: context.product.hashed_sku first, then
 * payload.hashedSku, context.product.id, and finally payload.id.
 *
 * Resolution order (spec-defined):
 *   context.product.hashed_sku ?? payload.hashedSku ?? context.product.id ?? payload.id
 */
export function mapPickerSelectionToUnavailableAlternative({
  payload,
  context,
  quantity,
}: {
  payload: Payload;
  context?: ProductSearchSelectionContext;
  quantity: number;
}): UnavailableAlternative {
  const selectedStore = context?.selectedStore;
  const unitPrice = selectedStore?.price ?? 0;

  const sku =
    context?.product?.hashed_sku ??
    payload.hashedSku ??
    undefined;

  return {
    catalog_id: sku ?? context?.product?.id ?? payload.id,
    image_url: selectedStore?.images?.catalog ?? selectedStore?.image_url ?? "",
    name: selectedStore?.name ?? payload.name ?? "Producto seleccionado",
    price: unitPrice,
    quantity,
    total_price: unitPrice * quantity,
    slug: context?.product?.slug ?? payload.id,
    sku,
  };
}
